
import Phaser from 'phaser';
import Player from './Player';
import GameScene from '../scenes/Game';
import { NodeConfig } from './NodeConfig';
import { NodeConfigFactory } from './NodeConfigFactory';

export class NodeDisplay {

    // Constants: Node display
    private static readonly TEXT_Y_OFFSET: number = 37;
    private static readonly TEXT_FONT_SIZE: number = 20;
    private static readonly TEXT_STROKE_COLOR: string = '#2a2a2a';
    private static readonly TEXT_STROKE_WIDTH: number = 5;
    private static readonly NODE_CIRCLE_RADIUS: number = 32;
    private static readonly NODE_VIEW_Y_OFFSET: number = -5;
    private static readonly NODE_DEPTH: number = 2;

    // Constants: Debug
    private static readonly DEBUG_LINE_WIDTH: number = 1;
    private static readonly DEBUG_LINE_COLOR: number = 0xdddddd;
    private static readonly DEBUG_LINE_ALPHA: number = 1;
    private static readonly DEBUG_TEXT_X_OFFSET: number = -40;
    private static readonly DEBUG_TEXT_Y_OFFSET: number = 15;

    // Constants: Temple empty sprite
    private static readonly TEMPLE_EMPTY_SPRITE_FRAME: number = 16;

    private scene: GameScene;
    private nodeContainer: Phaser.GameObjects.Container;
    private player: Player;
    private config: NodeConfig;
    private text?: Phaser.GameObjects.Text;
    private nodeCircle?: Phaser.GameObjects.Arc;
    private nodeView?: Phaser.GameObjects.Sprite;
    private spriteFrame: number;
    private nodeCircleInteractiveRadius: number; // This needs to be passed in or derived
    private counter: number;

    constructor(scene: GameScene, nodeContainer: Phaser.GameObjects.Container, player: Player, config: NodeConfig, initialCounter: number, interactiveRadius: number) {
        this.scene = scene;
        this.nodeContainer = nodeContainer;
        this.player = player;
        this.config = config;
        this.spriteFrame = config.spriteFrame;
        this.nodeCircleInteractiveRadius = interactiveRadius; // Initialize it
        this.counter = initialCounter;
    }

    public create() {
        this.createText(this.counter);
        this.createNodeCircle();
        this.createNodeView();
        this.addDisplayObjectsToContainer();
        this.setDepth();
        this.createDebugObjects(this.nodeContainer.x, this.nodeContainer.y, this.nodeContainer.name);
    }

    private createText(initialCounter: number) {
        this.text = this.scene.add.text(0, NodeDisplay.TEXT_Y_OFFSET, initialCounter.toString())
            .setStyle({
                fontFamily: 'arial',
                color: 'white',
                fontSize: NodeDisplay.TEXT_FONT_SIZE
            })
            .setStroke(NodeDisplay.TEXT_STROKE_COLOR, NodeDisplay.TEXT_STROKE_WIDTH)
            .setOrigin(0.5);
    }

    private createNodeCircle() {
        this.nodeCircle = this.scene.add.circle(0, 0, NodeDisplay.NODE_CIRCLE_RADIUS, this.player.color);
    }

    private createNodeView() {
        this.nodeView = this.scene.add.sprite(0, NodeDisplay.NODE_VIEW_Y_OFFSET, 'node_type', this.spriteFrame);
    }

    private addDisplayObjectsToContainer() {
        if (this.nodeCircle) this.nodeContainer.add(this.nodeCircle);
        if (this.nodeView) this.nodeContainer.add(this.nodeView);
        if (this.text) this.nodeContainer.add(this.text);
    }

    private setDepth() {
        this.nodeContainer.setDepth(NodeDisplay.NODE_DEPTH);
    }

    private createDebugObjects(x: number, y: number, id: string) {
        if (this.scene.getBootloaderScene().globals.debugMode) {
            const graphics = this.scene.add.graphics();
            graphics.lineStyle(NodeDisplay.DEBUG_LINE_WIDTH, NodeDisplay.DEBUG_LINE_COLOR, NodeDisplay.DEBUG_LINE_ALPHA);
            graphics.strokeCircle(x, y, this.nodeCircleInteractiveRadius * this.nodeContainer.scale);

            const debugInfo = this.scene.add.text(NodeDisplay.DEBUG_TEXT_X_OFFSET, NodeDisplay.DEBUG_TEXT_Y_OFFSET, id);
            this.nodeContainer.add(debugInfo);
        }
    }

    public updateDisplay(counter: number, playerColor: number, spriteFrame: number) {
        this.text?.setText(counter.toString());
        this.nodeCircle?.setFillStyle(playerColor);
        this.nodeView?.setFrame(spriteFrame);
    }

    public setVisibleText(visible: boolean) {
        this.text?.setVisible(visible);
    }

    public updateTempleEmptySpriteFrame() {
        if (this.config.type === NodeConfigFactory.Types.Temple) {
            this.spriteFrame = NodeDisplay.TEMPLE_EMPTY_SPRITE_FRAME;
        }
    }

    public getSpriteFrame(): number {
        return this.spriteFrame;
    }
}
